import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@rudi/api/react';

// Chat's model settings for the patient chat, reached through the Cortex
// backend (`/api/chat-model`). Chat stores one setting per environment.

export interface ChatModelChoice {
  model: string;
  attachment_model: string | null;
  fallback_model: string | null;
}

export interface ChatModelSettings {
  environment: string | null;
  openrouter_configured: boolean;
  configured_providers?: string[];
  default_model: string;
  /** Stored values; null while nobody has saved a choice. */
  model: string | null;
  attachment_model: string | null;
  fallback_model: string | null;
  /** What Chat uses now: the saved choice or the environment default. */
  effective: ChatModelChoice;
  revision: number;
  updated_by: string | null;
  updated_at: string | null;
}

export interface OpenRouterModel {
  provider?: string;
  id: string;
  name: string;
  context_length: number | null;
  /** USD per million tokens; null when OpenRouter publishes no price. */
  prompt_price_per_million: number | null;
  completion_price_per_million: number | null;
  input_modalities: string[];
  supports_images: boolean;
  supports_pdfs: boolean;
  supports_reasoning: boolean;
}

export interface OpenRouterModelList {
  fetched_at: string | null;
  models: OpenRouterModel[];
}

export interface SaveChatModel extends ChatModelChoice {
  revision: number;
}

export const CHAT_MODEL_KEY = ['chat-model'] as const;
export const CHAT_MODEL_LIST_KEY = ['chat-model', 'models'] as const;

const UNSUPPORTED = 'This Chat returned an unsupported chat model format.';

function status(error: unknown): number | undefined {
  return (error as { response?: { status?: number } })?.response?.status;
}

/** True when this Chat release, or this Cortex, has no chat model settings. */
function unavailable(error: unknown): boolean {
  const code = status(error);
  return code === 404 || code === 405 || code === 501;
}

export function isChatModelConflict(error: unknown): boolean {
  return status(error) === 409;
}

/** Chat's own reason, which explains a failed probe on 422. */
export function chatModelError(error: unknown): string {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  // FastAPI reports body validation errors as a list.
  if (Array.isArray(detail)) {
    const messages = detail
      .map(item => (item as { msg?: unknown })?.msg)
      .filter((msg): msg is string => typeof msg === 'string');
    if (messages.length) return messages.join(' ');
  }
  return error instanceof Error ? error.message : 'Could not save the chat model';
}

const optionalId = (value: unknown) => value === null || (typeof value === 'string' && !!value);
const optionalText = (value: unknown) => value === null || typeof value === 'string';

function parseSettings(data: ChatModelSettings): ChatModelSettings {
  const effective = data?.effective;
  if (
    !optionalText(data?.environment) ||
    typeof data.openrouter_configured !== 'boolean' ||
    typeof data.default_model !== 'string' ||
    !data.default_model ||
    !optionalId(data.model) ||
    !optionalId(data.attachment_model) ||
    !optionalId(data.fallback_model) ||
    typeof effective?.model !== 'string' ||
    !effective.model ||
    !optionalId(effective.attachment_model) ||
    !optionalId(effective.fallback_model) ||
    !Number.isSafeInteger(data.revision) ||
    data.revision < 0 ||
    !optionalText(data.updated_by) ||
    !optionalText(data.updated_at)
  )
    throw new Error(UNSUPPORTED);
  return data;
}

const price = (value: unknown) =>
  value === null || (typeof value === 'number' && Number.isFinite(value) && value >= 0);

function isModel(value: unknown): value is OpenRouterModel {
  const model = value as OpenRouterModel | null;
  return (
    typeof model?.id === 'string' &&
    !!model.id &&
    typeof model.name === 'string' &&
    (model.context_length === null || Number.isFinite(model.context_length)) &&
    price(model.prompt_price_per_million) &&
    price(model.completion_price_per_million) &&
    Array.isArray(model.input_modalities) &&
    model.input_modalities.every(item => typeof item === 'string') &&
    typeof model.supports_images === 'boolean' &&
    typeof model.supports_pdfs === 'boolean'
  );
}

function parseList(data: OpenRouterModelList): OpenRouterModelList {
  if (!Array.isArray(data?.models) || !optionalText(data.fetched_at ?? null))
    throw new Error('This Chat returned an unsupported model list format.');
  // Skip single entries Cortex cannot show rather than losing the whole list.
  return {
    fetched_at: data.fetched_at ?? null,
    models: data.models.filter(isModel).map(model => ({
      ...model,
      supports_reasoning: model.supports_reasoning === true,
    })),
  };
}

export function useChatModel() {
  const api = useApiClient();
  return useQuery({
    queryKey: CHAT_MODEL_KEY,
    queryFn: async () => {
      try {
        return parseSettings((await api.get<ChatModelSettings>('/chat-model')).data);
      } catch (error) {
        if (unavailable(error)) return null;
        throw error;
      }
    },
  });
}

export function useChatModelList(enabled = true) {
  const api = useApiClient();
  return useQuery({
    queryKey: CHAT_MODEL_LIST_KEY,
    enabled,
    queryFn: async () => {
      try {
        return parseList((await api.get<OpenRouterModelList>('/chat-model/models')).data);
      } catch (error) {
        if (unavailable(error)) return null;
        throw error;
      }
    },
  });
}

export function useSaveChatModel() {
  const api = useApiClient();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (payload: SaveChatModel) =>
      parseSettings((await api.put<ChatModelSettings>('/chat-model', payload)).data),
    onSuccess: saved => {
      client.setQueryData(CHAT_MODEL_KEY, saved);
    },
    onError: async error => {
      if (isChatModelConflict(error))
        await client.invalidateQueries({ queryKey: CHAT_MODEL_KEY, exact: true });
    },
  });
}
